import { useCurrentPlatformContext } from "@/contexts";
import { hasCapability } from "@/utils/capability";

import { useActiveScopeCapabilities } from "../capability/useActiveScopeCapabilities";

/**
 * User surfaces (`USER:READ` to see, `USER:WRITE` to create / update / delete /
 * deactivate) checked against the active scope's capability set. Strict RBAC —
 * the backend resolver is the single source of truth and the S0 grants give
 * every legacy member the matching keys. See docs/capabilities.md.
 *
 * On top of the capability check, `canCreateUser` also requires the current
 * platform's `enableUserCreation` product setting (superadmin-only platform
 * Extras switch, see docs/update-platform-page.md). The WPR platforms (USA
 * Padel, NPL, Padel Canada, Padel Quebec) mint accounts from a WPR ID or an
 * email instead, so with the switch off the dashboard offers neither creating a
 * brand-new account NOR attaching an existing one — both surfaces read this one
 * flag. The setting is a product gate, not a capability: absence (a backend that
 * has not shipped the field, or no resolved platform) reads as allowed so it can
 * never hide the surface by accident. The backend answers 403 on `POST /users`
 * and on the add-to-federation endpoint regardless.
 */
export const useUserPermissions = () => {
  const { capabilities } = useActiveScopeCapabilities();
  // Read through the context (not `useCurrentPlatform`, which throws on a null
  // platform): this hook also runs in the shell, where platform resolution can
  // still be settling.
  const { platform } = useCurrentPlatformContext();

  const canWriteUsers = hasCapability(capabilities, "USER:WRITE");

  const canSeeUsers = hasCapability(capabilities, "USER:READ");
  const canCreateUser = canWriteUsers && (platform?.enableUserCreation ?? true);
  const canUpdateUser = canWriteUsers;
  const canDeleteUser = canWriteUsers;
  const canDeactivateUser = canWriteUsers;

  return {
    canSeeUsers,
    canCreateUser,
    canUpdateUser,
    canDeleteUser,
    canDeactivateUser,
  };
};
